import FileOverridePrompt from 'features/Integrations/features/AudioManager/components/FileOverridePrompt/FileOverridePrompt';
import SelectedFileControl from '../FileSelect/components/SelectedFileControl/SelectedFileControl';
import { useAudioRecorder } from 'features/Integrations/features/AudioManager/hooks/useAudioRecorder';
import { useEffect, useRef, useState } from 'react';
import { useSendRecording } from 'features/Integrations/features/AudioManager/hooks/useSendRecording';
import RecordNew from './components/RecordNew/RecordNew';
import { toast } from 'react-toastify';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';
import { getAudioNotificationSubject } from '../../utils/audioFiles.utils';
import { getRecordingUploadError } from '../../utils/recording.utils';

export const MODES = {
  none: 0,
  'record-ready': 1,
};

const DEFAULT_MAX_DURATION_MS = 2 * 1000 * 60;

const AudioRecorder = ({
  usedNames,
  fileType,
  maxDuration = DEFAULT_MAX_DURATION_MS,
  clear,
  onChange = (blob, name) => {},
  onPlay = () => {},
  onSave = () => {},
}) => {
  const { uploadProgress, sendRecording } = useSendRecording();
  const { blob, recording, duration, micError, toggleRecording, clearAudio } =
    useAudioRecorder(fileType);

  const audioRef = useRef(null);
  const timeoutRef = useRef(null);

  const [mode, setMode] = useState(MODES['none']);
  const [fileName, setFileName] = useState('');
  const [playing, setPlaying] = useState(false);
  const [duplicateFile, setDuplicateFile] = useState(false);
  const [promptOverride, setPromptOverride] = useState(false);

  const onClear = () => {
    setMode(MODES['none']);
    clearAudio();
  };

  useEffect(() => {
    onChange(blob, fileName);
  }, [blob, fileName, onChange]);

  useEffect(() => {
    if (clear) {
      clearAudio();
      setMode(MODES['none']);
    }
  }, [clear]);

  useEffect(() => {
    if (uploadProgress !== null) setMode(MODES['record-ready']);
    else setMode(MODES['none']);
  }, [uploadProgress]);

  useEffect(() => {
    audioRef.current.addEventListener('play', onRefPlayed);
    audioRef.current.addEventListener('pause', onRefStopped);
    audioRef.current.addEventListener('ended', onRefStopped);

    return () => {
      audioRef.current?.removeEventListener('play', onRefPlayed);
      audioRef.current?.removeEventListener('pause', onRefStopped);
      audioRef.current?.removeEventListener('ended', onRefStopped);
    };
  }, []);

  // Saving Recorded Audio
  const onUploadNew = async () => {
    const validationError = getRecordingUploadError(blob, fileName);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      const ret = await sendRecording(blob, fileName, 'mp3');

      if (ret?.data?.expireTime)
        timeoutRef.current = setTimeout(() => {
          clearAudio();
        }, ret.data.expireTime);

      setDuplicateFile(ret?.data?.duplicateFile || false);
      notifySuccessToast(getAudioNotificationSubject(fileName), {
        isAddOperation: true,
      });
    } catch (error) {
      notifyErrorToast(getAudioNotificationSubject(fileName), {
        isAddOperation: true,
      });
    }
  };

  const onPlayback = () => {
    audioRef.current.src = URL.createObjectURL(blob);
    audioRef.current.play();
  };

  const onStopPlayback = () => {
    audioRef.current.pause();
    audioRef.current.src = null;
    setPlaying(false);
    URL.revokeObjectURL(blob);
  };

  const startSave = () => {
    if (!duplicateFile) {
      onSaveInt(false);
      return;
    }

    setPromptOverride(true);
  };

  const onSaveInt = (override) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setPromptOverride(false);
    onSave(false, override);
    onClear();
  };

  const onPlayInt = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    onPlay();
    onClear();
  };

  const onRefPlayed = () => setPlaying(true);
  const onRefStopped = () => setPlaying(false);

  return (
    <div className="flex-fill d-flex">
      {mode === MODES['none'] && (
        <RecordNew
          duration={duration}
          maxDuration={maxDuration}
          fileName={fileName}
          onFileNameChange={(v) => setFileName(v)}
          onClear={onClear}
          onUpload={onUploadNew}
          toggleRecording={toggleRecording}
          recording={recording}
          onPlayback={onPlayback}
          onStopPlayback={onStopPlayback}
          playing={playing}
          errMsg={micError}
        />
      )}

      {mode === MODES['record-ready'] && (
        <SelectedFileControl
          file={{ name: fileName, size: blob?.size }}
          progress={uploadProgress}
          onClear={onClear}
          onPlay={onPlayInt}
          onSave={startSave}
        />
      )}

      <FileOverridePrompt
        fileName={fileName}
        open={promptOverride}
        onClose={() => setPromptOverride(false)}
        onResolve={onSaveInt}
      />

      <audio ref={audioRef} autoPlay />
    </div>
  );
};

export default AudioRecorder;
